import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const UIContext = createContext(undefined);

export const UIProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [activeModal, setActiveModal] = useState(null);
  const [modalPayload, setModalPayload] = useState(null);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (type, message, duration = 4000) => {
      const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const toast = { id, type, message, duration };

      setToasts((prev) => [...prev, toast]);

      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [removeToast]
  );

  const openModal = useCallback((modalId, payload = null) => {
    setActiveModal(modalId);
    setModalPayload(payload);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setModalPayload(null);
  }, []);

  const value = useMemo(
    () => ({
      toasts,
      showToast,
      removeToast,
      activeModal,
      modalPayload,
      openModal,
      closeModal,
    }),
    [toasts, showToast, removeToast, activeModal, modalPayload, openModal, closeModal]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

export default UIContext;
